import React, { useState, useRef, useCallback, useEffect } from 'react';
import { DomSessionRow } from '@/types/domSession';
import { SessionsList } from './SessionsList';
import SessionDetail from './SessionDetail';
import { Loader2, Video, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

interface SessionsTableViewProps {
  sessions: DomSessionRow[];
  selectedSession: DomSessionRow | null;
  isLoading: boolean;
  onSessionSelect: (session: DomSessionRow) => void;
  onSessionHover?: (session: DomSessionRow) => void;
  isLoadingDetail?: boolean;
  total: number;
  page: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  onPageChange: (page: number) => void;
}

export const SessionsTableView: React.FC<SessionsTableViewProps> = ({
  sessions,
  selectedSession,
  isLoading,
  onSessionSelect,
  onSessionHover,
  isLoadingDetail,
  total,
  page,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  onPageChange,
}) => {
  const playerApiRef = useRef<{ goto: (timeInSeconds: number) => void } | null>(null);
  const [pendingSeek, setPendingSeek] = useState<number | null>(null);
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  // Reset player API when session changes
  useEffect(() => {
    playerApiRef.current = null;
    setPendingSeek(null);
  }, [selectedSession?.id]);

  useEffect(() => {
    if (!isMobile) {
      setMobileView('list');
    }
  }, [isMobile]);

  const handlePlayerReady = useCallback((api: { goto: (timeInSeconds: number) => void }) => {
    console.log('SessionsTableView: Player ready callback called with API:', api);
    playerApiRef.current = api;
    console.log('SessionsTableView: playerApiRef.current set to:', playerApiRef.current);
    if (pendingSeek != null) {
      console.log('SessionsTableView: applying pending seek on ready:', pendingSeek);
      try {
        api.goto(pendingSeek);
      } finally {
        setPendingSeek(null);
      }
    }
  }, [pendingSeek]);

  return (
    <div className="flex h-full">
      {/* Left Column - Demos List */}
      {(!isMobile || mobileView === 'list') && (
        <div className={`${isMobile ? "w-full" : "w-[350px]"} border-r card-border flex flex-col card-bg`}>
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <h3 className="text-sm font-medium text-foreground">Demos</h3>
            <span className="text-xs text-muted-foreground">{total}</span>
          </div>
          <div className="flex-1 overflow-auto">
            <SessionsList
              sessions={sessions}
              selectedSession={selectedSession}
              isLoading={isLoading}
              onSessionSelect={(session) => {
                onSessionSelect(session);
                if (isMobile) {
                  setMobileView('detail');
                }
              }}
              onSessionHover={onSessionHover}
            />
          </div>
          <div className="border-t border-border px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {startItem}-{endItem} of {total}
              </span>
              {pageSizeOptions && onPageSizeChange && (
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="text-xs text-muted-foreground bg-transparent border border-border rounded px-1.5 py-0.5 cursor-pointer hover:text-foreground transition-colors"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs text-muted-foreground min-w-[3ch] text-center">
                  {page}/{totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right Column - Demo Details (SessionDetail now contains its own right sidebar with tabs) */}
      {(!isMobile || mobileView === 'detail') && (
        <div className="flex-1 flex flex-col card-bg">
          {isMobile && (
            <div className="border-b card-border px-4 py-2 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileView('list')}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <span className="text-sm text-muted-foreground">Demo details</span>
            </div>
          )}
        {isLoading && !sessions.length ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : selectedSession ? (
          <SessionDetail
            session={selectedSession}
            onPlayerReady={handlePlayerReady}
            isLoadingDetail={isLoadingDetail}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Select a demo to view details
          </div>
        )}
        </div>
      )}
    </div>
  );
};
